import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Star, Clock, ChevronLeft, ChevronRight, CheckCircle, XCircle, Timer } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { Button } from '../../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../components/ui/table';
import reviewConstants from '../../../../../constants/review.constants';
import { BigHead } from '../../../../../lib/core.cjs.development';
import { cn } from '../../../../../lib/utils';
import apiService from '../../../../../services/api.service';
import useEmployeesData from '../../hooks/useEmployeesData';
import EmployeesFilterV3 from './EmployeesFilter';

dayjs.extend(relativeTime);

const REVIEW_STATUS_CONFIG = {
  [reviewConstants.REVIEW_STATUSES.CONFIRMED]: {
    label: 'Confirmed',
    className: 'bg-green-500/10 text-green-600',
    icon: CheckCircle,
  },
  [reviewConstants.REVIEW_STATUSES.DECLINED]: {
    label: 'Declined',
    className: 'bg-red-500/10 text-red-600',
    icon: XCircle,
  },
  [reviewConstants.REVIEW_STATUSES.PROCESSED]: {
    label: 'Processed',
    className: 'bg-amber-500/10 text-amber-600',
    icon: Timer,
  },
  [reviewConstants.REVIEW_STATUSES.PENDING]: {
    label: 'Pending',
    className: 'bg-blue-500/10 text-blue-600',
    icon: Clock,
  },
  [reviewConstants.REVIEW_STATUSES.REVERTED]: {
    label: 'Reverted',
    className: 'bg-gray-500/10 text-gray-600',
    icon: null,
  },
};

function RecentReviews({ startPickerValue, endPickerValue }) {
  const [reviewHistoryData, setReviewHistoryData] = useState([]);
  const [reviewsTableCount, setReviewsTableCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const { department } = useParams();

  const { employeesOptions, pickedEmployees, fetchEmployees, setPickedEmployees } =
    useEmployeesData();

  const fetchReviewHistory = async () => {
    try {
      setIsLoading(true);

      const departments = await apiService.developer.getDepartments();
      const headDepartment = departments.find((el) => el.name === department);

      const userIdsString = pickedEmployees.map((user) => user.id).join(',');

      const query = [
        `startDate=${startPickerValue.toISOString()}`,
        `endDate=${endPickerValue.toISOString()}`,
        `skip=${currentPage * rowsPerPage}`,
        `limit=${rowsPerPage}`,
        `sort=updatedAt`,
        `userIds=${userIdsString}`,
        `departmentIds=${headDepartment?._id || null}`,
      ].join('&');

      const response = await apiService.head.getHeadDashboardReviews(query);

      setReviewHistoryData(response.paginatedResult || []);
      setReviewsTableCount(response.totalCount || 0);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReviewHistory();
  }, [pickedEmployees, startPickerValue, endPickerValue, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(reviewsTableCount / rowsPerPage);

  // Filter out reverted reviews
  const filteredReviews = reviewHistoryData.filter(
    (review) => review.status !== reviewConstants.REVIEW_STATUSES.REVERTED,
  );

  return (
    <Card style={{ border: '1px solid hsl(var(--border))' }}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Star className="h-5 w-5 text-amber-500" />
            Recent Reviews
          </CardTitle>
          <div className="w-64">
            <EmployeesFilterV3
              employeesOptions={employeesOptions}
              pickedEmployees={pickedEmployees}
              fetchEmployees={fetchEmployees}
              setPickedEmployees={setPickedEmployees}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Review Goal</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Progress</TableHead>
                <TableHead className="text-right">Confirmed</TableHead>
                <TableHead className="text-right">Declined</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading &&
                Array.from({ length: 5 }).map((_, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={index}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-muted animate-pulse rounded-full" />
                        <div className="h-4 w-32 bg-muted animate-pulse rounded" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                    </TableCell>
                    <TableCell>
                      <div className="h-6 w-20 bg-muted animate-pulse rounded-full" />
                    </TableCell>
                    <TableCell>
                      <div className="h-4 w-16 bg-muted animate-pulse rounded mx-auto" />
                    </TableCell>
                    <TableCell>
                      <div className="h-4 w-8 bg-muted animate-pulse rounded ml-auto" />
                    </TableCell>
                    <TableCell>
                      <div className="h-4 w-8 bg-muted animate-pulse rounded ml-auto" />
                    </TableCell>
                    <TableCell>
                      <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                    </TableCell>
                  </TableRow>
                ))}
              {!isLoading && filteredReviews.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No reviews found for the selected period
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                filteredReviews.length > 0 &&
                filteredReviews.map((review) => {
                  const statusConfig =
                    REVIEW_STATUS_CONFIG[review.status] || REVIEW_STATUS_CONFIG.PENDING;
                  const StatusIcon = statusConfig.icon;

                  return (
                    <TableRow key={review._id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full overflow-hidden bg-muted flex-shrink-0">
                            <BigHead {...review.avatar} />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{review.email}</p>
                            {review.department && (
                              <p className="text-xs text-muted-foreground">{review.department}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-medium">{review.reviewGoal || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium',
                            statusConfig.className,
                          )}
                        >
                          {StatusIcon && <StatusIcon className="h-3 w-3" />}
                          {statusConfig.label}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary rounded-full transition-all"
                              style={{ width: `${review.progress || 0}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground w-8">
                            {review.progress || 0}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <span
                          className={[
                            'inline-flex items-center justify-center min-w-[24px]',
                            'px-1.5 py-0.5 rounded text-xs font-medium',
                            'bg-green-500/10 text-green-600',
                          ].join(' ')}
                        >
                          {review.confirmed || 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span
                          className={[
                            'inline-flex items-center justify-center min-w-[24px]',
                            'px-1.5 py-0.5 rounded text-xs font-medium',
                            'bg-red-500/10 text-red-600',
                          ].join(' ')}
                        >
                          {review.declined || 0}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-muted-foreground text-sm">
                          <Clock className="h-3 w-3" />
                          {dayjs(review.updatedAt).fromNow()}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {reviewsTableCount > 0 && (
          <div className="flex items-center justify-between pt-4 border-t mt-4">
            <p className="text-sm text-muted-foreground">
              Showing {currentPage * rowsPerPage + 1} to{' '}
              {Math.min((currentPage + 1) * rowsPerPage, reviewsTableCount)} of {reviewsTableCount}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => prev - 1)}
                disabled={currentPage === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => prev + 1)}
                disabled={currentPage >= totalPages - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

RecentReviews.propTypes = {
  startPickerValue: PropTypes.instanceOf(dayjs).isRequired,
  endPickerValue: PropTypes.instanceOf(dayjs).isRequired,
};

export default RecentReviews;
